import React from 'react';
import { CONSTANTS } from '../data.js';
import { iconFor } from '../lib/utils';

type TopBarProps = {
  currentEraName: string;
  discoveredCount: number;
  totalElements: number;
  onResetBoard: () => void;
  onToggleMainMenu: () => void;
  gamePhase?: string;
  playerScore?: number;
  bossScore?: number;
  currentBoss?: any;
  statusText?: string;
  playerAvatar: string;
};

export const TopBar: React.FC<TopBarProps> = ({
  currentEraName,
  discoveredCount,
  totalElements,
  onResetBoard,
  onToggleMainMenu,
  gamePhase = 'normal',
  playerScore = 0,
  bossScore = 0,
  currentBoss,
  statusText = '',
  playerAvatar,
}) => {
  const isBossBattle = gamePhase.startsWith('boss');
  
  // Get player name from avatar
  const playerAvatarData = CONSTANTS.player.avatars.find(avatar => avatar.key === playerAvatar);
  const playerName = playerAvatarData?.name || CONSTANTS.player.name;

  return (
    <div 
      className="w-full h-14 flex items-center justify-between px-6 border-b-2"
      style={{
        background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.background} 0%, ${CONSTANTS.ui.theme.colors.surface} 100%)`,
        borderColor: CONSTANTS.ui.theme.colors.border,
        boxShadow: `0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(218, 165, 32, 0.1)`
      }}
    >
      <div className="flex items-center gap-6">
        <span 
          className="font-cinzel text-xl font-bold tracking-wide"
          style={{ 
            color: CONSTANTS.ui.theme.colors.primary,
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
          }}
        >
          {CONSTANTS.gameName}
        </span>
        
        {!isBossBattle && (
          <>
            <div className="flex items-center gap-2">
              <span 
                className="font-crimson text-sm"
                style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
              >
                Era:
              </span>
              <span 
                className="font-crimson font-semibold px-3 py-1 rounded-md text-sm"
                style={{ 
                  color: CONSTANTS.ui.theme.colors.text,
                  background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary}20 0%, ${CONSTANTS.ui.theme.colors.accent}20 100%)`,
                  border: `1px solid ${CONSTANTS.ui.theme.colors.border}40`
                }}
              >
                {currentEraName}
              </span>
            </div>

            {/* Player Info Display */}
            <div className="flex items-center gap-3">
              <img 
                src={iconFor(playerAvatar)} 
                alt={playerName} 
                className="w-8 h-8 rounded-full border bg-black/30" 
                style={{borderColor: CONSTANTS.ui.theme.colors.primary}} 
              />
              <span 
                className="font-crimson text-sm font-semibold"
                style={{ color: CONSTANTS.ui.theme.colors.text }}
              >
                {playerName}
              </span>
            </div>
          </>
        )}

        {isBossBattle && currentBoss && (
          <div className="flex items-center gap-4">
            <span 
              className="font-cinzel text-lg font-bold"
              style={{ color: CONSTANTS.ui.theme.colors.error }}
            >
              Boss Battle: {currentBoss.name}
            </span>
            <div className="flex items-center gap-2">
              <span className="font-crimson text-sm" style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}>
                Score:
              </span>
              <div className="flex gap-1">
                {Array.from({length: CONSTANTS.cardGame.playerRoundsToWin}).map((_, i) => (
                  <div key={i} className={`w-3 h-3 rounded-full ${i < playerScore ? 'bg-green-500' : 'bg-black/50'}`} />
                ))}
              </div>
              <span className="font-crimson text-sm mx-2" style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}>vs</span>
              <div className="flex gap-1">
                {Array.from({length: CONSTANTS.cardGame.bossRoundsToWin}).map((_, i) => (
                  <div key={i} className={`w-3 h-3 rounded-full ${i < bossScore ? 'bg-red-500' : 'bg-black/50'}`} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-4">
        {isBossBattle && statusText && (
          <div 
            className="font-crimson text-sm italic px-3 py-1 rounded-md"
            style={{ 
              color: CONSTANTS.ui.theme.colors.text,
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface}80 0%, ${CONSTANTS.ui.theme.colors.background}80 100%)`,
              border: `1px solid ${CONSTANTS.ui.theme.colors.border}40`
            }}
          >
            {statusText}
          </div>
        )}

        {!isBossBattle && (
          <div 
            className="font-crimson text-sm px-3 py-1 rounded-md"
            style={{ 
              color: CONSTANTS.ui.theme.colors.text,
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.success}20 0%, ${CONSTANTS.ui.theme.colors.success}30 100%)`,
              border: `1px solid ${CONSTANTS.ui.theme.colors.success}40`
            }}
          >
            Discovered {discoveredCount} / {totalElements}
          </div>
        )}
        
        {!isBossBattle && (
          <button 
            onClick={onResetBoard} 
            className="font-crimson px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.warning} 0%, #ff7f00 100%)`,
              color: CONSTANTS.ui.theme.colors.background,
              border: `1px solid ${CONSTANTS.ui.theme.colors.warning}`,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
          >
            Clear Board
          </button>
        )}
        
        <button 
          onClick={onToggleMainMenu} 
          className="font-crimson px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, #3d3426 100%)`,
            color: CONSTANTS.ui.theme.colors.text,
            border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
          }}
        >
          Menu
        </button>
      </div>
    </div>
  );
};
